import * as _ from "lodash";
import { put, select } from "redux-saga/effects";

import actions from "@/actions";
import type { MarkerPosition } from "@/models/marker-position";
import * as LightService from "@/services/light";

interface RotateLightsAction {
    payload: {
        direction: string;
    };
}

/**
 * Rotates the selected lights and markers 90 degrees.
 *
 * @param {RotateLightsAction} action - The dispatched action.
 * @yields Selects light/marker positions and dispatches rotated coordinates.
 */
function* rotateLights(action: RotateLightsAction) {
    const { present, mode, selectedMarkers } = yield select(
        ({ map }: { map: unknown }) => {
            const { general, values } = map as {
                general: {
                    mode: { name: string };
                    selectedMarkers: string[];
                };
                values: Record<string, { present: unknown }>;
            };

            const modeName = general.mode.name;
            const store = modeName === "view" ? "global" : "mode";

            return {
                present: _.cloneDeep(values[store].present) as {
                    lights: Record<string, unknown>;
                    markers: Record<string, MarkerPosition>;
                    groupGeometries: {
                        vertices: { lat: number; lng: number }[];
                    }[];
                },
                mode: modeName,
                selectedMarkers: general.selectedMarkers,
            };
        },
    );

    const remainingLights = _.values(present.lights);

    if (mode === "view") {
        const remainingMarkers = _.values(present.markers);

        const {
            lights: rotateLightsCollection,
            geometries,
            markers,
        } = LightService.rotateLights(
            remainingLights,
            present.groupGeometries,
            -90,
            remainingMarkers,
        );

        yield put(
            actions.map.values.global.updateLights(rotateLightsCollection),
        );
        yield put(actions.map.values.global.setGroupGeometries(geometries));

        const markersById = _.keyBy(
            markers.map((m) => ({ ...m, changed: true })),
            "uniqueId",
        );

        yield put(actions.map.values.global.setMarkers(markersById));
    } else {
        if (selectedMarkers.length > 0) {
            const direction = action.payload?.direction;
            const delta = direction === "left" ? -90 : 90;

            const updatedMarkers: Record<string, MarkerPosition> = {};

            for (const markerId of selectedMarkers) {
                const marker = present.markers[markerId];
                if (!marker) continue;

                const newAngle = ((marker.angle ?? 0) + delta + 360) % 360;

                updatedMarkers[markerId] = {
                    ...marker,
                    angle: newAngle,
                    changed: true,
                };
            }

            if (Object.keys(updatedMarkers).length > 0) {
                yield put(
                    actions.map.values.mode.updateMarkers(updatedMarkers),
                );
            }

            return;
        }

        const selectedLights = _.remove(
            remainingLights,
            (light: { selected: boolean }) => light.selected,
        );

        _.forEach(selectedLights, (selectedLight: { rotate: () => void }) =>
            selectedLight.rotate(),
        );

        let anyOverlapped = false;

        _.forEach(
            selectedLights,
            (selectedLight: {
                isOverlapping: (lights: unknown[]) => boolean;
            }) => {
                if (
                    !anyOverlapped &&
                    selectedLight.isOverlapping([
                        ...remainingLights,
                        ...selectedLights,
                    ])
                ) {
                    anyOverlapped = true;
                }
            },
        );

        if (anyOverlapped) {
            yield put(
                actions.window.showMessage(
                    "We couldn't rotate all selected lights. The skipped lights were either out of bounds of the map dimensions, or overlapped existing lights.",
                    "warning",
                ),
            );
        } else {
            yield put(actions.map.values.mode.updateLights(selectedLights));
        }
    }
}

export default rotateLights;
